"use client";

import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { AuthAccent } from "./portal-auth-accent";
import {
  ACCENT,
  ACCENT_SHADOW,
  INK,
  INPUT_BG,
  INPUT_BORDER,
} from "./login-shared";

export const AUTH_LABEL_STYLE: CSSProperties = {
  fontFamily:
    "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontSize: 10,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: "rgba(11,15,25,0.5)",
};

const ICON_WRAP_STYLE: CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  color: "rgba(11,15,25,0.4)",
  display: "inline-flex",
  pointerEvents: "none",
};

function MailIcon(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function LockIcon(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function Spinner(): ReactElement {
  return (
    <svg
      className="mklogin-spinner"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="1.6"
      />
      <path
        d="M8 2 a 6 6 0 0 1 6 6"
        fill="none"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

type AuthTextInputProps = {
  readonly id: string;
  readonly label: string;
  readonly type?: "email" | "password" | "text";
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly autoComplete?: string;
  readonly disabled?: boolean;
  readonly icon?: "mail" | "lock";
};

export function AuthTextInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  disabled = false,
  icon,
}: AuthTextInputProps): ReactElement {
  return (
    <div>
      <label
        htmlFor={id}
        style={{ ...AUTH_LABEL_STYLE, display: "block", marginBottom: 8 }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        {icon ? (
          <span style={{ ...ICON_WRAP_STYLE, left: 14 }}>
            {icon === "mail" ? <MailIcon /> : <LockIcon />}
          </span>
        ) : null}
        <input
          id={id}
          className="mklogin-input"
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          style={{
            width: "100%",
            height: 50,
            boxSizing: "border-box",
            border: `1px solid ${INPUT_BORDER}`,
            borderRadius: 11,
            background: INPUT_BG,
            fontSize: 14.5,
            fontFamily: "inherit",
            color: INK,
            outline: "none",
            padding: icon ? "0 14px 0 42px" : "0 14px",
          }}
        />
      </div>
    </div>
  );
}

type AuthSubmitButtonProps = {
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  /** Portal: org brand accent instead of the staff default. */
  readonly accent?: AuthAccent | null;
};

export function AuthSubmitButton({
  children,
  disabled = false,
  loading = false,
  accent = null,
}: AuthSubmitButtonProps): ReactElement {
  return (
    <button
      type="submit"
      className="mklogin-submit"
      disabled={disabled || loading}
      style={{
        marginTop: 6,
        height: 54,
        width: "100%",
        borderRadius: 12,
        border: "none",
        background: accent?.accent ?? ACCENT,
        color: accent?.accentText ?? "#fff",
        fontFamily: "inherit",
        fontSize: 15.5,
        fontWeight: 600,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 9,
        boxShadow: `0 12px 26px -12px ${accent?.accentShadow ?? ACCENT_SHADOW}`,
      }}
    >
      {loading ? (
        <>
          <Spinner />
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
}

export function AuthErrorBanner({
  children,
}: { readonly children: ReactNode }): ReactElement {
  return (
    <div
      role="alert"
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: "1px solid rgba(192,57,43,0.28)",
        background: "rgba(192,57,43,0.06)",
        color: "#a23b2e",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

export function AuthInfoBanner({
  children,
}: { readonly children: ReactNode }): ReactElement {
  return (
    <div
      role="status"
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: "1px solid rgba(11,15,25,0.14)",
        background: "rgba(11,15,25,0.04)",
        color: "rgba(11,15,25,0.72)",
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}
